import api from './api';
import { normalizeLanguage } from '../constants/Languages';

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const normalizeManagementMembership = (membership) => {
  const minorUnits = Number(membership.planPriceMinorUnits);
  const hasExplicitPrice = membership.price != null && membership.price !== '';
  const price = hasExplicitPrice
    ? (Number(membership.price) || 0)
    : (Number.isFinite(minorUnits) ? minorUnits / 100 : 0);

  return {
    id: membership.id,
    storeId: membership.storeId,
    customerId: membership.customerId,
    customerName: membership.customerName ?? '',
    customerEmail: membership.customerEmail ?? '',
    membershipPlanId: membership.membershipPlanId,
    planName: normalizeLocalizedDictionary(membership.planName),
    price,
    currency: membership.currency || membership.planCurrency || undefined,
    status: membership.status,
    paymentChannel: membership.paymentChannel,
    currentPeriodStartUtc: membership.currentPeriodStartUtc ?? null,
    currentPeriodEndUtc: membership.currentPeriodEndUtc ?? null,
    manuallyPaidThroughUtc: membership.manuallyPaidThroughUtc ?? null,
    canMarkPeriodPaid: Boolean(membership.canMarkPeriodPaid),
    isAdvancePeriodPayment: Boolean(membership.isAdvancePeriodPayment),
    cancelAtPeriodEnd: Boolean(membership.cancelAtPeriodEnd),
    cancelsAtUtc: membership.cancelsAtUtc ?? null,
    canCancel: Boolean(membership.canCancel),
    canResumeCancellation: Boolean(membership.canResumeCancellation),
  };
};

const withManagementStore = (storeId) => ({
  _authContext: 'management',
  _storeId: storeId,
});

export const getManagementMemberships = async (storeId) => {
  const response = await api.get('/membership-plans/customer-memberships/management', withManagementStore(storeId));

  return (response.data || []).map(normalizeManagementMembership);
};

export const createInStoreMembership = async (storeId, payload) => {
  const response = await api.post('/membership-plans/customer-memberships/in-store', payload, withManagementStore(storeId));

  return normalizeManagementMembership(response.data);
};

const refreshMembership = async (storeId, membershipId) => {
  const memberships = await getManagementMemberships(storeId);
  return memberships.find((membership) => membership.id === membershipId) ?? null;
};

export const markMembershipPeriodPaid = async (storeId, membershipId, checkout) => {
  const payload = typeof checkout === 'string'
    ? { paymentMethod: checkout }
    : {
      paymentMethod: checkout?.paymentMethod,
      payments: checkout?.payments,
    };

  await api.post(
    `/membership-plans/customer-memberships/${membershipId}/mark-period-paid`,
    payload,
    withManagementStore(storeId),
  );

  return refreshMembership(storeId, membershipId);
};

export const cancelStoreCustomerMembership = async (storeId, membershipId) => {
  await api.post(
    `/membership-plans/customer-memberships/${membershipId}/cancel`,
    {},
    withManagementStore(storeId),
  );

  return refreshMembership(storeId, membershipId);
};

export const resumeStoreCustomerMembershipCancellation = async (storeId, membershipId) => {
  await api.post(
    `/membership-plans/customer-memberships/${membershipId}/resume-cancellation`,
    {},
    withManagementStore(storeId),
  );

  return refreshMembership(storeId, membershipId);
};

const storeCustomerMembershipService = {
  getManagementMemberships,
  createInStoreMembership,
  markMembershipPeriodPaid,
  cancelStoreCustomerMembership,
  resumeStoreCustomerMembershipCancellation,
};

export default storeCustomerMembershipService;
